import loadGoogleMaps from "js/google-maps/google-maps"
import {BranchMapSettings, BranchMapPopup, BranchMapMarkers} from "@components/sections/map-section/map-section-interfaces"
import {filterBranchesCols, limitBranchesList} from "@components/sections/map-section/map-section-branches"
import {MarkerClusterer} from "@googlemaps/markerclusterer"

let map: google.maps.Map | undefined | null
let gMarkers: google.maps.marker.AdvancedMarkerElement[] = []
let markerClusterer: MarkerClusterer | null = null
let lastIcon: google.maps.marker.AdvancedMarkerElement

const mapElement = document.getElementById("map")
const mapSettings: BranchMapSettings = JSON.parse(mapElement?.getAttribute("data-map") ?? "")
const changeMarkersCheckboxes = document.querySelectorAll<HTMLInputElement>(".js-change-map-markers input")
let markersType: string = ""
const markers = JSON.parse(mapElement?.getAttribute("data-markers") ?? "").filter(
  (item: BranchMapMarkers) => item.lat != null && item.lng != null,
)

changeMarkersCheckboxes.forEach((checkbox) => {
  if (checkbox.checked) markersType = checkbox.value

  checkbox.addEventListener("change", () => {
    markersType = checkbox.value
    if (map) {
      reloadMarkers(markersType, map) // Check if map is initialized before calling this function
    }
    filterBranchesCols(markersType)
  })
})

const setMarkers = async (markersType: string, map: google.maps.Map) => {
  const {InfoWindow} = (await google.maps.importLibrary("maps")) as google.maps.MapsLibrary
  const {AdvancedMarkerElement} = (await google.maps.importLibrary("marker")) as google.maps.MarkerLibrary

  const infoWindow = new InfoWindow({
    maxWidth: 328,
    minWidth: 296,
  })

  // Clear any existing markers
  gMarkers.forEach((marker) => (marker.map = null))
  gMarkers = []

  // Loop through the markers and create them
  markers?.forEach((item: BranchMapMarkers) => {
    if (markersType !== "" && item.type !== markersType) return

    const marker = new AdvancedMarkerElement({
      map: map,
      position: {
        lat: Number(item.lat),
        lng: Number(item.lng),
      },
      content: setIcon(item.icon.src),
    })

    gMarkers.push(marker)

    marker.addListener("gmp-click", () => {
      setDefaultIcon(item)
      lastIcon = marker
      marker.content = setIcon(item.icon_active.src)
      infoWindow.close()
      infoWindow.setContent(popupElement(item.popup))
      const mapInstance = marker.map
      if (mapInstance) {
        if (marker.position) infoWindow.setPosition(marker.position)
        infoWindow.open(mapInstance)
      } else {
        console.error("Failed to get map instance.")
      }
    })
  })

  // Initialize MarkerClusterer (only after markers are created)
  if (markerClusterer) {
    markerClusterer.clearMarkers() // Remove previous markers from the clusterer
  }

  // Create a new MarkerClusterer with the updated markers
  markerClusterer = new MarkerClusterer({
    markers: gMarkers,
    map: map,
  })
}

const reloadMarkers = (markersType: string, map: google.maps.Map) => {
  gMarkers.forEach((marker) => {
    marker.map = null
  })

  gMarkers = []

  setMarkers(markersType, map)
}

const setIcon = (iconSrc: string) => {
  const markerSvg = document.createElement("img")
  markerSvg.src = iconSrc

  return markerSvg
}

const setDefaultIcon = (item: BranchMapMarkers) => {
  if (lastIcon) lastIcon.content = setIcon(item.icon.src)
}

const disableDefaultUI = () => {
  if (map) {
    const mobileQuery = window.matchMedia("(max-width: 36em)")

    if (!mobileQuery.matches) {
      map.setOptions({
        disableDefaultUI: false,
        streetViewControl: true,
        mapTypeControl: true,
      })
    } else {
      map.setOptions({
        disableDefaultUI: true,
        streetViewControl: false,
        mapTypeControl: false,
      })
    }
  }
}

const popupElement = (popup: BranchMapPopup): string => {
  const content: string = `
  <div class="i-map-branch u-border u-border-default p-2 u-rounded-2 u-posr overflow-hidden u-radius-sm d-grid gap-3 align-items-top h-100" data-type="b2b">
    ${popup.img ? `<a href="${popup.url}" class="i-map-branch__img-link u-img-hover d-block u-radius-xs">
      <div class="img u-posr overflow-hidden d-block  --placeholder --cover  --ratio-4-3 u-radius-xs">
        <picture>
        <img src="${popup.img.src ? popup.img.src : ""}" alt="" width="192" height="144" draggable="false" data-ll-status="loaded" class="entered loaded">
        </picture>
      </div>
      </a>` : ""}
    <div class="i-map-branch__content d-flex justify-content-between gap-1">
      <div class="i-map-branch__content-left d-flex flex-column gap-2">
        <div class="d-flex flex-column gap-1">
          <h3 class="base-heading mb-0 u-fw-700 u-text-balance u-h5">
            <a class="i-map-branch__heading-link u-clamp d-block u-color-text u-hover:color-text u-underline-transparent u-hover:underline-non-transparent" href="${popup.url}" title="${popup.title}">${popup.title}</a>
          </h3>
          <div>
            ${popup.description ? `<p class="u-fz-sm">${popup.description}</p>` : ""}
            ${popup.address ? `<p class="u-fz-sm">${popup.address.street ? popup.address.street + "," : ""} ${popup.address.zip ? popup.address.zip + "," : ""} ${popup.address.city}</p>` : ""}
          </div>
        </div>
        <div class="u-fz-sm">
          <span class="d-block mb-1 u-fw-600">Otevírací doba:</span>
          <table>
            <tbody>
            ${
              popup.opening_hours_short
                ? Object.entries(popup.opening_hours_short)
                    .map(([label, time]) => {
                      const item = `
                <tr>
                  <th class="u-fw-400">${label}:</th>
                  <td class="px-1">${time}</td>
                </tr>`
                      return item
                    })
                    .join("")
                : ""
            }
            </tbody>
          </table>
        </div>
      </div>
      <div class="i-map-branch__content-right d-flex flex-column justify-content-between flex-shrink-0 align-items-end">
        <div class="d-flex flex-column gap-1 align-items-end">
        ${
          popup.label
            ? `<span class="i-tag d-inline-flex gap-1 align-items-center justify-content-center text-center u-fw-600  --sm    text-uppercase">
            <span class="i-tag__title">${popup.label}</span>
          </span>`
            : ""
        }
        </div>
        <a class="btn u-posr u-cursor-pointer d-inline-flex gap-1 align-items-center justify-content-center flex-nowrap flex-shrink-0 --primary --sm   --icon-only" href="${popup.url}" title="Detail prodejny">
          <span class="btn__icon d-flex align-items-center justify-content-center">
            <span class="icon d-flex align-items-center justify-content-center --sm">
              <svg xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 320 512">
                <path d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z"></path>
              </svg>
            </span>
          </span>
        </a>
      </div>
    </div>
  </div>`
  return content
}

const initMap = async () => {
  if (!mapElement) return

  const google = await loadGoogleMaps()

  map = new google.maps.Map(mapElement, {
    mapId: mapSettings.map_id,
    center: mapSettings.center,
    zoom: mapSettings.zoom,
    fullscreenControl: false,
    streetViewControl: false,
    zoomControlOptions: {
      position: google.maps.ControlPosition.LEFT_BOTTOM,
    },
  })

  disableDefaultUI()

  window.addEventListener("resize", () => {
    disableDefaultUI()
  })

  const latlngbounds = new google.maps.LatLngBounds()
  if (changeMarkersCheckboxes.length) {
    filterBranchesCols(markersType)
  } else {
    limitBranchesList()
  }

  markers?.forEach((marker: {lat: number; lng: number}) => {
    const latLng = new google.maps.LatLng(marker.lat, marker.lng)
    latlngbounds.extend(latLng)
  })

  setMarkers(markersType, map)

  if (markers.length > 1) {
    map.setCenter(latlngbounds.getCenter())
    map.fitBounds(latlngbounds)
  }

  mapElement.classList.remove("--loading")
}

initMap()
